/**
 * /continue/:assessmentId — post-assessment package overview.
 *
 * Public page (no auth). Reached via the "Pick my package" CTA in the
 * assessment results email. Flow:
 *   1. Greet parent + athlete by name, echo their stated goal.
 *   2. ONE question: "Which feels realistic?" (5 options — informational only).
 *   3. Show live `pricing_blocks` so the parent sees current rates;
 *      sale closes in person with the coach.
 *
 * Legacy "Pick one" recommendation cards + one-click checkout removed
 * 2026-02-23 — pricing is now exclusively driven by the `pricing_blocks`
 * collection so edits in /coach/pricing-blocks flow through everywhere.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    Sparkles,
    Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
    { key: "1_per_week", label: "1 time a week", subtitle: "Once is what works." },
    { key: "at_least_1", label: "For sure at least 1 a week", subtitle: "Open to more if it goes well." },
    { key: "maybe_2",    label: "Maybe 2 a week", subtitle: "Flexible — depends on the week." },
    { key: "sure_2",     label: "For sure 2 a week", subtitle: "Locked in for twice weekly." },
    { key: "more_than_2", label: "More than 2 — that's what I can do", subtitle: "Go time. Bring the volume." },
];

function dollars(cents) {
    return `$${(Number(cents) / 100).toFixed(2)}`;
}

export default function ContinuePage() {
    const { assessmentId } = useParams();
    const [ctx, setCtx] = useState(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState(null);
    const [frequency, setFrequency] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/continue/${assessmentId}/context`);
                setCtx(r.data);
            } catch (e) {
                setError(e?.response?.data?.detail || "Couldn't load your results");
            } finally {
                setBusy(false);
            }
        })();
    }, [assessmentId]);

    if (busy) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <h1 className="font-display text-3xl tracking-wider text-white mb-3">
                        Hmm, we couldn't find that.
                    </h1>
                    <p className="text-sm text-zinc-300">{error}</p>
                    <p className="text-xs text-zinc-500 mt-6">
                        If this is a mistake, reply to the email or text the gym — we'll sort it.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="continue-page" className="min-h-screen bg-brand-obsidian text-white pb-20">
            {/* Header */}
            <section className="border-b border-zinc-900 pt-14 pb-10">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3">
                        <Sparkles className="w-3 h-3 inline-block mr-1.5 -mt-0.5" />
                        Next step
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider leading-[1] text-white">
                        {ctx.parent_first_name}, let's get {ctx.athlete_first_name} set up.
                    </h1>
                    <p className="mt-4 text-base text-zinc-300 leading-relaxed">
                        One quick question and we'll match you to the right schedule + package. You can change your mind at checkout.
                    </p>
                    {ctx.goals && (
                        <div className="mt-5 border-l-2 border-brand-red bg-zinc-950 px-4 py-3 rounded-r">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1">You told us</div>
                            <p className="text-sm text-zinc-200 italic">"{ctx.goals}"</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Question */}
            <section className="py-10">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <div className="flex items-baseline gap-2 mb-5">
                        <span className="font-display text-2xl tracking-wider text-brand-red">Q1</span>
                        <h2 className="font-display text-2xl md:text-3xl tracking-wider text-white">
                            Which feels realistic?
                        </h2>
                    </div>
                    <div className="grid gap-3">
                        {FREQUENCY_OPTIONS.map((opt) => {
                            const isActive = frequency === opt.key;
                            return (
                                <button
                                    type="button"
                                    key={opt.key}
                                    data-testid={`freq-${opt.key}`}
                                    onClick={() => setFrequency(opt.key)}
                                    className={`text-left w-full px-5 py-4 border rounded-xl transition-all flex items-center justify-between gap-3 ${
                                        isActive
                                            ? "border-brand-red bg-brand-red/10"
                                            : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                                    }`}
                                >
                                    <div>
                                        <div className={`font-display text-lg tracking-wider ${isActive ? "text-white" : "text-zinc-200"}`}>
                                            {opt.label}
                                        </div>
                                        <div className="text-[12px] text-zinc-400 mt-0.5">{opt.subtitle}</div>
                                    </div>
                                    {isActive && (
                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-red text-white">
                                            <Check className="w-4 h-4" />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Live pricing blocks — drives off /coach/pricing-blocks. */}
            {Array.isArray(ctx?.live_blocks) && ctx.live_blocks.length > 0 && (
                <section className="py-8 border-t border-zinc-900" data-testid="continue-live-blocks">
                    <div className="max-w-3xl mx-auto px-6 md:px-10">
                        <div className="flex items-baseline gap-2 mb-2">
                            <span className="font-display text-2xl tracking-wider text-brand-red">All our blocks</span>
                            <h2 className="font-display text-xl tracking-wider text-zinc-300">— current pricing.</h2>
                        </div>
                        <p className="text-[12px] text-zinc-500 mb-5">
                            These are what we run live in the gym. Pricing here updates the moment we adjust our blocks — so what you see is always current.
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            {ctx.live_blocks.map((b) => {
                                const sub = b.subscription;
                                const hasPackages = Array.isArray(b.packages) && b.packages.length > 0;
                                return (
                                    <div
                                        key={b.block_id}
                                        data-testid={`live-block-${b.block_id}`}
                                        className={`rounded-2xl border-2 ${b.is_fallback ? "border-amber-700/50" : "border-zinc-800"} bg-zinc-950 overflow-hidden`}
                                    >
                                        <div className="px-5 py-4 border-b border-zinc-900">
                                            <h3 className="font-display text-lg tracking-wider text-white leading-tight">{b.name}</h3>
                                            {b.description && (
                                                <p className="text-[12px] text-zinc-400 mt-1.5 leading-snug">{b.description}</p>
                                            )}
                                        </div>
                                        {sub && (
                                            <div className="px-5 py-4 border-b border-zinc-900">
                                                <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-400 mb-1.5">Subscription</div>
                                                <div className="text-2xl font-display tracking-wider text-white">
                                                    {dollars(sub.price_cents)}
                                                    <span className="text-sm text-zinc-400 font-normal">
                                                        /{({ daily: "day", weekly: "wk", monthly: "mo" })[sub.interval] || sub.interval}
                                                    </span>
                                                </div>
                                                <div className="text-[11px] text-zinc-500 mt-1">
                                                    {sub.commitment_count} {({ daily: "day", weekly: "week", monthly: "month" })[sub.interval] || sub.interval}{sub.commitment_count === 1 ? "" : "s"} commitment
                                                    {sub.allow_upfront && sub.allow_recurring && " · pay weekly or upfront"}
                                                    {sub.allow_upfront && !sub.allow_recurring && " · upfront only"}
                                                    {!sub.allow_upfront && sub.allow_recurring && " · auto-bill"}
                                                </div>
                                            </div>
                                        )}
                                        {hasPackages && (
                                            <div className="px-5 py-4">
                                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Packs</div>
                                                <div className="space-y-1.5">
                                                    {b.packages.map((p, i) => (
                                                        <div key={i} className="flex items-baseline justify-between text-sm">
                                                            <span className="text-zinc-200">{p.sessions} sessions</span>
                                                            <span className="font-mono text-white">
                                                                {dollars(p.sessions * p.per_session_cents)}
                                                                <span className="text-[11px] text-zinc-500"> · {dollars(p.per_session_cents)}/ea</span>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {b.reactivation_fee_cents > 0 && (
                                            <div className="px-5 py-2.5 border-t border-zinc-900 text-[10px] text-amber-300/80">
                                                + {dollars(b.reactivation_fee_cents)} reactivation fee applies if you&apos;ve lapsed
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-5 text-center">
                            Your coach will help you pick the right block in person. The cards above are quick-pick starters tied to your assessment.
                        </p>
                    </div>
                </section>
            )}

            {/* Bottom — talk to a coach escape hatch */}
            <section className="py-10 mt-10 border-t border-zinc-900">
                <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
                    <p className="text-sm text-zinc-300">
                        Want to chat first? Reply to the assessment email or text the gym — we'll talk through any of these.
                    </p>
                </div>
            </section>
        </div>
    );
}
